import React, { CSSProperties, useRef, useState, useEffect } from 'react';
import cx from 'classnames';

import './lazy-image.css';

/**
 * @typedef {LazyImageProps}
 * @property {string} alt HTML alt tag for the img element
 * @property {className} string classes to apply to the img element
 * @property {CSSProperties} style styles to apply to the img element
 * @property {string} src the URL for the image
 */
interface LazyImageProps {
    alt: string;
    className: string;
    style: CSSProperties;
    src: string;
}

/**
 * An img element that includes a CSS transition when the image loads. When updating an image in
 * in place, note that you will need to provide a unique `src` property to reload the image.
 * If the image URL does not change, an easy way to do this is to append a hash to the URL that you
 * update when the file contents have changed, like this:
 *
 *      <LazyImage
 *          src={`${url}?{hash}`}
 *          ...
 *      />
 *
 * For an example, see: packages/web/src/components/pages/Account/components/AccountHeaderContent/AccountAvatar.tsx
 * @param props {LazyImageProps}
 */
export const LazyImage = ({
    alt,
    className,
    style,
    src,
}: LazyImageProps): JSX.Element => {
    const imgRef = useRef<HTMLImageElement>(null);
    const [hide, setHide] = useState(false);
    const onLoad = (): void => {
        setTimeout((): void => {
            setHide(false);
        }, 1);
    };

    useEffect(() => {
        if (imgRef.current) {
            const currentSrc = imgRef?.current?.src ?? null;

            if (currentSrc === null) {
                imgRef.current.src = src;
            } else if (currentSrc !== src) {
                setHide(true);
                imgRef.current.src = src;
            }
        }
    }, [imgRef?.current, src]);

    const classNames = cx('lazy-image', className, {
        hide,
    });

    return (
        <img
            alt={alt}
            className={classNames}
            ref={imgRef}
            style={style}
            onLoad={onLoad}
        />
    );
};
